import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import usePermissions from "@/hooks/use-permissions";
import useTranslations from "@/hooks/use-translations";
import { store_collections_path } from "@/lib/routes";
import { AdminPageProps } from "@/types";
import { SheetProps } from "@/types/ui";
import { useForm, usePage } from "@inertiajs/react";
import { Plus } from "lucide-react";
import { FormEvent } from "react";
import CollectionForm, { CollectionFormData } from "./collection-form";

export default function NewCollectionSheet({ open, onOpenChange }: SheetProps) {
  const { current_store } = usePage<AdminPageProps>().props;
  const { can } = usePermissions();
  const { t } = useTranslations("shared.components.new_collection_sheet");

  const newCollectionForm = useForm<CollectionFormData>({
    collection: { name: "", state: "draft", product_ids: [], products: [] },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    newCollectionForm.post(store_collections_path(current_store!), {
      invalidateCacheTags: ["stores", current_store?.id ?? ""],
      onSuccess() {
        newCollectionForm.resetAndClearErrors();
        onOpenChange(false);
      },
    });
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>
          <Button variant="default" disabled={!can("create_collection")}>
            <Plus />
            {t("title")}
          </Button>
        </SheetTrigger>

        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t("submit_title")}</SheetTitle>
          </SheetHeader>

          <CollectionForm form={newCollectionForm} onSubmit={onSubmit} />

          <SheetFooter>
            <Button
              type="submit"
              form="new-collection-form"
              disabled={newCollectionForm.processing}
            >
              {newCollectionForm.processing && <Spinner />}
              {t("shared.actions.save")}
            </Button>
            <SheetClose asChild>
              <Button variant="outline" disabled={newCollectionForm.processing}>
                {t("shared.actions.close")}
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
